import React from 'react';
import { Star, MapPin, Award, Shield, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const suppliers = [
  {
    id: 1,
    name: 'TechGov Solutions',
    description: 'IT services and cybersecurity solutions for government agencies',
    rating: 4.8,
    reviews: 156,
    location: 'Washington, DC',
    certifications: ['8(a)', 'SDVOSB', 'ISO 27001'],
    categories: ['IT Services', 'Cybersecurity', 'Cloud Migration'],
    image: 'https://ui-avatars.com/api/?name=TechGov+Solutions',
  },
  {
    id: 2,
    name: 'Federal Compliance Experts',
    description: 'Compliance consulting and certification assistance',
    rating: 4.6,
    reviews: 89,
    location: 'Arlington, VA',
    certifications: ['GSA Schedule', 'CMMC'],
    categories: ['Compliance', 'Consulting', 'Training'],
    image: 'https://ui-avatars.com/api/?name=Federal+Compliance',
  },
];

export function SuppliersSection() {
  const navigate = useNavigate();

  const handleViewProfile = (supplierId: number) => {
    navigate(`/marketplace/suppliers/${supplierId}`);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {suppliers.map((supplier) => (
          <div
            key={supplier.id}
            className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
          >
            <div className="flex items-center space-x-4 border-b border-gray-200 p-6">
              <img
                src={supplier.image}
                alt={supplier.name}
                className="h-16 w-16 flex-shrink-0 rounded-lg bg-gray-200"
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-medium text-gray-900">{supplier.name}</h3>
                <div className="mt-1 flex items-center">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.floor(supplier.rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-gray-200 text-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-500">
                    {supplier.rating} ({supplier.reviews} reviews)
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-1 flex-col justify-between p-6">
              <div className="flex-1">
                <p className="text-sm text-gray-500">{supplier.description}</p>
                <div className="mt-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                    {supplier.location}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {supplier.certifications.map((cert) => (
                      <span
                        key={cert}
                        className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                      >
                        <Shield className="mr-1 h-3 w-3" />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {supplier.categories.map((category) => (
                      <span
                        key={category}
                        className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => handleViewProfile(supplier.id)}
                  className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Profile
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}